import React, { FC, useMemo } from 'react';
import * as yup from 'yup';
import NumericInput from '../../../../../components/library/inputs/numeric/NumericInput';
import { noop } from '../../../../../utilities/FunctionUtilities';
import DateTime from '../../../../../components/library/inputs/dateTime/DateTime';
import TextInput from '../../../../../components/library/inputs/text/TextInput';
import SelectInput from '../../../../../components/library/inputs/select/SelectInput';
import { Button } from 'react-bootstrap';
import { FastField, FieldArray, Formik, FormikProps, isObject } from 'formik';
import { RootState } from '../../../../../store';
import { FormModel } from '../../../../../models/form/form';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useIntl } from 'react-intl';
import Feedback from '../../../../../components/library/inputs/feedback/Feedback';
// interface Props {}

const DiscountCodes: FC<any> = React.forwardRef<FormikProps<any>>((props, ref) => {
  const form = useSelector((state: RootState) => state.formAdmin.form) as FormModel;
  const intl = useIntl();

  const validationSchema = useMemo(() => {
    return yup.object().shape({
      discountCodes: yup.array().of(
        yup.object().shape({
          code: yup.string().required(intl.formatMessage({ id: 'discountCodes.errors.codeRequired' })),
          quantity: yup
            .number()
            .typeError(intl.formatMessage({ id: 'discountCodes.errors.quantityInvalid' }))
            .nullable()
            .min(0, intl.formatMessage({ id: 'discountCodes.errors.quantityInvalid' })),
          expirationDate: yup
            .date()
            .typeError(intl.formatMessage({ id: 'discountCodes.errors.invalidExpirationDate' }))
            .nullable()
            .test(
              'date-strict-format',
              intl.formatMessage({ id: 'discountCodes.errors.invalidExpirationDate' }),
              (date, context) => {
                const { originalValue } = context as any;
                return (
                  !originalValue ||
                  moment(originalValue, 'YYYY-MM-DD', true).isValid() ||
                  moment(originalValue, 'YYYY-MM-DDT00:00:00', true).isValid()
                );
              },
            ),
          discount: yup.object().shape({
            type: yup.string(),
            value: yup
              .number()
              .required(intl.formatMessage({ id: 'discountCodes.errors.discountValueRequired' }))
              .min(0, intl.formatMessage({ id: 'discountCodes.errors.discountMin' }))
              .when('type', {
                is: (type) => type === 'percent',
                then: (schema) => schema.max(100, intl.formatMessage({ id: 'discountCodes.errors.discountMax' })),
              }),
          }),
        }) as any,
      ),
    });
  }, []);

  return (
    <>
      <div className="step-admin">
        <div className="section p-4 mb-3">
          <div className="section-title">{intl.formatMessage({ id: 'discountCodes.labels.discountCodes' })}</div>

          <Formik
            initialValues={{ discountCodes: form.discountCodes || [] }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={true}
            onSubmit={noop}
            innerRef={ref}
          >
            {({ values, setFieldValue, validateField, touched, errors }) => {
              return (
                <React.Fragment>
                  {values.discountCodes.length > 0 && (
                    <div>
                      <div className="d-flex align-items-center mb-1">
                        <div className="me-2" style={{ width: 200 }}>
                          {intl.formatMessage({ id: 'discountCodes.labels.code' })}
                        </div>
                        <div className="me-2 text-center" style={{ width: 100 }}>
                          {intl.formatMessage({ id: 'discountCodes.labels.quantity' })}
                        </div>
                        <div className="me-2" style={{ width: 180 }}>
                          {intl.formatMessage({ id: 'discountCodes.labels.expirationDate' })}
                        </div>
                        <div className="text-center" style={{ width: 150 }}>
                          {intl.formatMessage({ id: 'discountCodes.labels.discount' })}
                        </div>
                      </div>
                    </div>
                  )}
                  <FieldArray
                    name="discountCodes"
                    render={(arrayHelpers) => {
                      return (
                        <div>
                          {values.discountCodes.map((discount, index) => {
                            let isTouched = false;
                            let error = '';

                            if (touched.discountCodes && touched.discountCodes[index]) {
                              isTouched = true;
                            }

                            if (errors.discountCodes && errors.discountCodes[index]) {
                              const errorDefinition = errors.discountCodes[index];
                              const key = Object.keys(errorDefinition)[0];
                              if (isObject(errorDefinition[key])) {
                                error = errorDefinition[key][Object.keys(errorDefinition[key])[0]];
                              } else {
                                error = errorDefinition[Object.keys(errorDefinition)[0]];
                              }
                            }

                            return (
                              <div key={index} className="mb-2">
                                <div className="d-flex align-items-center">
                                  <div className="me-2" style={{ width: 200 }}>
                                    <TextInput
                                      id="code"
                                      enabled={true}
                                      placeholder=""
                                      value={discount.code}
                                      onChange={(event) => {
                                        setFieldValue(`discountCodes.${index}.code`, event.target.value.toUpperCase());
                                        validateField(`discountCodes`);
                                      }}
                                    />
                                  </div>
                                  <div className="me-2" style={{ width: 100 }}>
                                    <NumericInput
                                      id="quantity"
                                      enabled={true}
                                      placeholder={''}
                                      value={discount.quantity}
                                      onChange={(event) => {
                                        setFieldValue(`discountCodes.${index}.quantity`, event.target.value);
                                        validateField(`discountCodes`);
                                      }}
                                      minValue={0}
                                    />
                                  </div>
                                  <div className="me-2" style={{ width: 180 }}>
                                    <DateTime
                                      enabled={true}
                                      placeholder=""
                                      id={'expirationDate'}
                                      value={
                                        discount.expirationDate
                                          ? moment(discount.expirationDate).format('YYYY-MM-DD')
                                          : undefined
                                      }
                                      mode="date"
                                      onChange={(value) => {
                                        setFieldValue(`discountCodes.${index}.expirationDate`, value);
                                        validateField(`discountCodes`);
                                      }}
                                      style={{ height: 33.5 }}
                                    />
                                  </div>
                                  <div className="d-flex me-2" style={{ width: 150 }}>
                                    <div className="me-1 flex-1">
                                      <FastField name={`discountCodes.${index}.discount.value`}>
                                        {({ field }) => {
                                          return (
                                            <NumericInput
                                              id="discountValue"
                                              value={field.value}
                                              enabled={true}
                                              placeholder={''}
                                              onChange={(event) => {
                                                setFieldValue(field.name, event.target.value);
                                                validateField(`discountCodes`);
                                              }}
                                              minValue={0}
                                            />
                                          );
                                        }}
                                      </FastField>
                                    </div>
                                    <div className="flex-1">
                                      <FastField name={`discountCodes.${index}.discount.type`}>
                                        {({ field }) => {
                                          return (
                                            <SelectInput
                                              onChange={(event) => {
                                                setFieldValue(field.name, event.target.value);
                                                validateField(`discountCodes`);
                                              }}
                                              enabled={true}
                                              noEmptyValue={true}
                                              options={[
                                                { text: '$', value: 'amount' },
                                                { text: '%', value: 'percent' },
                                              ]}
                                              value={field.value}
                                            />
                                          );
                                        }}
                                      </FastField>
                                    </div>
                                  </div>
                                  <div>
                                    <Button variant="danger" onClick={() => arrayHelpers.remove(index)}>
                                      x
                                    </Button>
                                  </div>
                                </div>
                                <div>
                                  <Feedback touched={isTouched} error={error}></Feedback>
                                </div>
                              </div>
                            );
                          })}
                          <Button
                            variant="primary"
                            onClick={() => {
                              arrayHelpers.push({ discount: { type: 'amount' } });
                            }}
                          >
                            +
                          </Button>
                        </div>
                      );
                    }}
                  ></FieldArray>
                </React.Fragment>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
});

DiscountCodes.displayName = 'DiscountCodes';

export default DiscountCodes;
